import React from 'react';
import { Edit, Save, X, Calendar, Users, UserCog } from 'lucide-react';
import Button from '../UI/Button';
import { useAppSelector } from '../../store/hooks';

interface EventActionBarProps {
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onManageGuests: () => void;
  onManageUshers?: () => void;
  saving?: boolean;
  disabled?: boolean;
  editDisabledReason?: string;
  guestManagementDisabled?: boolean;
  guestManagementReason?: string;
  usherManagementDisabled?: boolean;
  usherManagementReason?: string;
  eventTitle?: string;
  eventStatus?: string;
  showUsherManagement?: boolean;
}

export const EventActionBar: React.FC<EventActionBarProps> = ({
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onManageGuests,
  onManageUshers,
  saving = false,
  disabled = false,
  editDisabledReason = '',
  guestManagementDisabled = false,
  guestManagementReason = '',
  usherManagementDisabled = false,
  usherManagementReason = '',
  eventTitle = 'Event',
  eventStatus = 'active',
  showUsherManagement = true
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  const primaryActions = isEditing ? [
    {
      label: 'Save Changes',
      icon: Save,
      onClick: onSave,
      variant: 'primary' as const,
      loading: saving,
      disabled: saving,
      title: 'Save your changes'
    },
    {
      label: 'Cancel',
      icon: X,
      onClick: onCancel,
      variant: 'secondary' as const,
      disabled: saving,
      title: 'Cancel editing'
    }
  ] : [
    {
      label: 'Edit Event',
      icon: Edit,
      onClick: onEdit,
      variant: 'primary' as const,
      disabled,
      title: disabled && editDisabledReason ? editDisabledReason : 'Edit event details'
    },
    {
      label: 'Manage Guests',
      icon: Users,
      onClick: onManageGuests,
      variant: 'secondary' as const,
      disabled: disabled || guestManagementDisabled,
      title: guestManagementDisabled ? guestManagementReason : 'Manage event guests'
    },
    ...(showUsherManagement && onManageUshers ? [{
      label: 'Manage Ushers',
      icon: UserCog,
      onClick: onManageUshers,
      variant: 'secondary' as const,
      disabled: disabled || usherManagementDisabled,
      title: usherManagementDisabled ? usherManagementReason : 'Manage ushers for attendance tracking'
    }] : [])
  ];

  if (isEditing) {
    return (
      <div className={`border rounded-xl p-3 sm:p-4 ${
        theme === 'dark' 
          ? 'bg-blue-900/30 border-blue-700/50' 
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center">
            <Edit className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
            }`} />
            <div>
              <h3 className={`text-xs sm:text-sm font-medium ${
                theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
              }`}>Editing Event</h3>
              <p className={`text-[10px] sm:text-xs ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-700'
              }`}>Make your changes and save when ready</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {primaryActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Button
                  key={index}
                  variant={action.variant}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className="flex items-center text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
                >
                  {'loading' in action && action.loading ? (
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1.5 sm:mr-2"></div>
                  ) : (
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">{action.label}</span>
                  <span className="sm:hidden">{action.label.split(' ')[0]}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-sm border p-3 sm:p-4 ${
      theme === 'dark' 
        ? 'bg-gray-900/80 border-gray-700/50' 
        : 'bg-white border-gray-200'
    }`}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
        
        {/* Event Info */}
        <div className="flex items-center">
          <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-400'
          }`} />
          <div>
            <h3 className={`text-xs sm:text-sm font-medium ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Event Actions</h3>
            <p className={`text-[10px] sm:text-xs ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>Manage and configure your event</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Primary Actions */}
          <div className="flex gap-2 flex-wrap">
            {primaryActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Button
                  key={index}
                  variant={action.variant}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className="flex items-center text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
                  title={action.title}
                >
                  <Icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  <span className="hidden sm:inline">{action.label}</span>
                  <span className="sm:hidden">{action.label.split(' ')[0]}</span>
                </Button>
              );
            })}
          </div>
          
        </div>
      </div>
    </div>
  );
};
